import { Attachment, AttachmentFactory } from "@/model";
import { getPdfThumbnail } from "@/components/preview_pdf/pdf2img";
import { FileLocalUrl, FileSignedUrl } from "@/store/file-store";

export const OFFICE_FILE_DICTIONARY = {
    doc: "application/msword",
    docm: "application/vnd.ms-word.document.macroenabled.12",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsm: "application/vnd.ms-excel.sheet.macroenabled.12",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xlsb: "application/vnd.ms-excel.sheet.binary.macroenabled.12",
    csv: "text/csv",
    ppt: "application/vnd.ms-powerpoint",
    pptm: "application/vnd.ms-powerpoint.presentation.macroenabled.12",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation"
};

export const OFFICE_TYPE_LIST = Object.values(OFFICE_FILE_DICTIONARY);

// mimeTypeがOffice系列か判定する
export const judgeOffice = ( type: string ) => {
    const _type = type?.toLocaleLowerCase();
    return OFFICE_TYPE_LIST.some( mimeType => _type == mimeType );
}

// マクロ入りのOfficeファイルか判定する
export const isMacroEnabled = ( type: string ) => {
    const _type = type?.toLocaleLowerCase();
    return _type?.includes('macroenabled') ?? false;
}

// 添付ファイルの種別判定
export const getFileType = (file: File | Attachment) => {
    const type = AttachmentFactory.isAttachment( file ) ? file.mime : file.type;
    if( type.startsWith("image/") ) {
        return "image";
    } else if( type.startsWith("video/") ) {
        return "video";
    } else if(type == "application/pdf"){
        return "pdf";
    } else if( judgeOffice(type) ) {
        return "office";
    } else {
        return AttachmentFactory.isAttachment( file ) ? "file" : "";
    }
}

// 添付ファイルの種別デフォルトアイコンを設定
export const getDefaultIcon = (file: File | Attachment) => {
    const type = getFileType(file);
    switch( type ) {
        case "video": {
            return `/img/icon/icon_file_movie.svg`;
        }
        case "image":
        case "pdf": {
            return `/img/icon/icon_file_${type}.svg`;
        }
        case "office": {
            const mimeType = ( AttachmentFactory.isAttachment( file ) ? file.mime : file.type )?.toLocaleLowerCase();
            const entry = Object.entries(OFFICE_FILE_DICTIONARY).find( entry => mimeType === entry[1] );
            const key = entry ? entry[0] : "file";
            return `/img/icon/icon_file_${key}.svg`
        }
        default: {
            return "";
        }
    }
}

export const getThumbnail = async (
    file: File,
): Promise<File | undefined> => {
    const type = "image/jpeg"
    switch(getFileType(file)) {
        case "pdf": {
            return await getPdfThumbnail(file);
        }
        case "video": {
            return await getMovieThumbnail(file);
        }
        default: {
            return undefined;
        }
    }
}

/**
 * 動画ファイルからサムネイルを作成する
 * @param file 動画ファイル
 * @param type サムネイルのMIMEタイプ
 * @param currentTime サムネイルを取得する時間（秒）
 */
const getMovieThumbnail = (
  file: File,
  currentTime = 0
): Promise<File | undefined> => {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')

    video.onloadeddata = () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      video.currentTime = currentTime
    }

    video.onseeked = async () => {
      context?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight)
      const type = 'image/jpeg';
      const thumbUrl = canvas.toDataURL(type)
      const blob = await (await fetch(thumbUrl)).blob()
      const fileName = `${file.name + file.size}_tmb.jpg`
      const thumbnailFile = new File([blob], fileName, { type: type })
      resolve(thumbnailFile)
    }

    video.onerror = (e) => {
      console.log('Video loading error: ', e)
      resolve(undefined)
    }

    video.src = URL.createObjectURL(file)
    video.load()
  })
}

/**
 * 署名付きURLからローカルURLを生成する
 * @param signedUrl 署名付きURL
 * @param fileName ファイル名
 * @returns ローカルURL
 */
export const getFileLocalUrl = async (signedUrl: string, fileName: string): Promise<string> => {
    if (!signedUrl) return '';

    try {
        const blob = await (await fetch(signedUrl, { mode: 'cors' })).blob();
        const file = new File([blob], fileName, { type: blob.type });
        return URL.createObjectURL(file);
    } catch(err: any) {
        console.log(err);
        return '';
    }
}

/**
 * ファイルの署名付きURLをローカルURLに変換する
 * @param dataList データリスト
 * @returns
 */
export const convertFileUrl = async (dataList: FileSignedUrl[]): Promise<FileLocalUrl[]> => {
    const ret = await Promise.all(dataList.map(async (data): Promise<FileLocalUrl> => {
        const pathList = data.key.split('/');
        const fileName = pathList[pathList.length - 1] ?? '';
        const url = await getFileLocalUrl(data.signedUrl, fileName);
        return {
            key: data.key,
            url: url,
        };
    }));
    return ret;
}
